import { useCallback } from 'react';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import fetch from 'features/common/fetch';
import {
	changeAllRecordsCheckedValueWithCheckAllbox,
	fetchRAAnalysis,
	loadMore,
	loadPage,
	loadSorted,
} from 'features/views/roboAdvisory/actions';
import { getCheckAllCheckbox, getRaAnalysis, getSort } from 'features/views/roboAdvisory/selectors';
import { getHasSubordinates } from 'features/views/user/selectors';
import { getCodelistByName } from 'utils';

import RATableRow from './RATableRow';

const fetchStorePath = fetch.constants.urls.raList;

const RATable = () => {
	const dispatch = useDispatch();

	const raTypesCodelist = useSelector(getCodelistByName(codelists.raAnalysisTypes));
	const raStatesCodelist = useSelector(getCodelistByName(codelists.raAnalysisStates));
	const raProductGroupsCodelist = useSelector(getCodelistByName(codelists.raProductGroups));

	const raAnalysisRecords = useSelector(getRaAnalysis);
	const hasRecords = !isNilOrEmpty(raAnalysisRecords);
	const isCheckAllChecked = useSelector(getCheckAllCheckbox);
	const sortValue = useSelector(getSort);
	const hasSubordinates = useSelector(getHasSubordinates);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPage(page - 1));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(fetchRAAnalysis());
	}, [dispatch]);

	const handleChangeCheckAllCheckbox = useCallback(() => {
		dispatch(changeAllRecordsCheckedValueWithCheckAllbox());
	}, [dispatch]);

	const handleSort = useCallback(
		(sort) => {
			dispatch(loadSorted(sort));
		},
		[dispatch],
	);

	return (
		<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleReload}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell />
						<TableCell>
							<div>{getLabelMessage('roboAdvisory:analysisType')}</div>
							<SortCell hasRecords={hasRecords} sortBy="state" sort={sortValue} onSort={handleSort}>
								{getLabelMessage('roboAdvisory:analysisState')}
							</SortCell>
						</TableCell>
						<TableCell>
							<SortCell hasRecords={hasRecords} sortBy="client" sort={sortValue} onSort={handleSort}>
								{getLabelMessage('roboAdvisory:client')}
							</SortCell>
						</TableCell>
						<TableCell>
							<div>{getLabelMessage('roboAdvisory:personIn')}</div>
							<div>{getLabelMessage('roboAdvisory:businessIn')}</div>
						</TableCell>
						<TableCell>{getLabelMessage('roboAdvisory:type')}</TableCell>
						<TableCell>{getLabelMessage('roboAdvisory:product')}</TableCell>
						<TableCell>
							<div>{getLabelMessage('roboAdvisory:municipality')}</div>
							<div>{getLabelMessage('roboAdvisory:zipCode')}</div>
						</TableCell>
						<TableCell>
							<SortCell hasRecords={hasRecords} sortBy="createdOn" sort={sortValue} onSort={handleSort}>
								{getLabelMessage('roboAdvisory:dateCreated')}
							</SortCell>
						</TableCell>
						{hasSubordinates && (
							<TableCell align="right">
								{getLabelMessage('common:selectAll')}
								<Checkbox checked={isCheckAllChecked} onChange={handleChangeCheckAllCheckbox} />
							</TableCell>
						)}
					</TableRow>
				</TableHead>
				<TableBody>
					{!hasRecords ? (
						<TableHasNoResults />
					) : (
						raAnalysisRecords.map((record, i) => (
							<TableRow key={i}>
								<RATableRow
									record={record}
									raTypesCodelist={raTypesCodelist}
									raStatesCodelist={raStatesCodelist}
									raProductGroupsCodelist={raProductGroupsCodelist}
								/>
							</TableRow>
						))
					)}
				</TableBody>
				<PaginationRow
					colSpan={100}
					fetchStorePath={fetchStorePath}
					onLoadNext={handleLoadMore}
					onPaginationClick={handlePaginationClick}
				/>
			</Table>
		</ComponentLoader>
	);
};

export default RATable;
